import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { UserEditModal } from '@/components/owner/UserEditModal';
import { AddUserModal } from '@/components/owner/AddUserModal';
import type { AppUser, UserRole } from '@/types';

export type UserRow = AppUser & { status?: string };

function roleLabel(r: string) {
  return r === 'owner'
    ? 'Owner'
    : r === 'admin'
      ? 'Admin'
      : r === 'recruiter'
        ? 'Reclutador'
        : r === 'pending'
          ? 'Pendiente'
          : r === 'viewer'
            ? 'Viewer'
            : r;
}

function statusLabel(s: string | undefined) {
  return s === 'pending' ? 'Pendiente' : s === 'disabled' ? 'Deshabilitado' : 'Activo';
}

function isPending(u: UserRow) {
  return u.role !== 'owner' && (u.status === 'pending' || u.companyId == null);
}

export function OwnerUsersPage() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<UserRow | null>(null);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [filterCompanyId, setFilterCompanyId] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(collection(db, 'users'));
      setUsers(
        snap.docs.map((d) => {
          const data = d.data();
          return {
            uid: d.id,
            email: data.email ?? '',
            displayName: data.displayName ?? '',
            companyId: data.companyId ?? null,
            role: (data.role as UserRole) ?? 'viewer',
            status: data.status ?? 'active',
            createdAt: data.createdAt ?? '',
          } as UserRow;
        })
      );
    } catch (err) {
      console.error('Firestore: error listando usuarios', err);
      setError(err instanceof Error ? err.message : 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'companies'));
      setCompanies(snap.docs.map((d) => ({ id: d.id, name: d.data().name ?? d.id })));
    } catch (err) {
      console.error('Firestore: error listando empresas', err);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const pendingUsers = users.filter(isPending);
  const companyName = (id: string | null) =>
    id ? companies.find((c) => c.id === id)?.name ?? id : '—';
  const q = searchQuery.trim().toLowerCase();
  const filteredUsers = users.filter((u) => {
    if (filterCompanyId && u.companyId !== filterCompanyId) return false;
    if (filterRole && u.role !== filterRole) return false;
    if (q) {
      const name = (u.displayName ?? '').toLowerCase();
      const email = (u.email ?? '').toLowerCase();
      const company = companyName(u.companyId).toLowerCase();
      const role = roleLabel(u.role).toLowerCase();
      if (!name.includes(q) && !email.includes(q) && !company.includes(q) && !role.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Usuarios</h1>
        <Button onClick={() => setAddUserOpen(true)}>Agregar usuario</Button>
      </div>

      {!loading && users.length > 0 && (
        <div className="max-w-sm">
          <Input
            type="search"
            placeholder="Buscar por nombre, email, empresa o rol..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9"
          />
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Error al cargar la lista</p>
          <p className="mt-1">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={loadUsers}>
            Reintentar
          </Button>
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground">Cargando...</p>
      ) : (
        <>
          {pendingUsers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Pendientes de asignación</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Usuarios con status pendiente o sin empresa. Asigna empresa y rol para activarlos.
                </p>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 font-medium">Nombre</th>
                      <th className="pb-2 font-medium">Email</th>
                      <th className="pb-2 font-medium">Rol actual</th>
                      <th className="pb-2 font-medium">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingUsers.map((u) => (
                      <tr key={u.uid} className="border-b">
                        <td className="py-2">{u.displayName || '—'}</td>
                        <td className="py-2">{u.email}</td>
                        <td className="py-2">{roleLabel(u.role)}</td>
                        <td className="py-2">
                          <Button variant="outline" size="sm" onClick={() => setEditingUser(u)}>
                            Asignar / Editar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <CardTitle className="text-base">Listado global</CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                    value={filterCompanyId}
                    onChange={(e) => setFilterCompanyId(e.target.value)}
                  >
                    <option value="">Todas las empresas</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <select
                    className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                  >
                    <option value="">Todos los roles</option>
                    <option value="owner">Owner</option>
                    <option value="admin">Admin</option>
                    <option value="recruiter">Reclutador</option>
                    <option value="viewer">Viewer</option>
                    <option value="pending">Pendiente</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredUsers.length === 0 ? (
                <p className="text-muted-foreground">
                  {users.length === 0
                    ? 'No hay usuarios registrados.'
                    : 'Ningún usuario coincide con los filtros.'}
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 font-medium">Nombre</th>
                      <th className="pb-2 font-medium">Email</th>
                      <th className="pb-2 font-medium">Rol</th>
                      <th className="pb-2 font-medium">Empresa</th>
                      <th className="pb-2 font-medium">Estado</th>
                      <th className="pb-2 font-medium">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.uid} className="border-b">
                        <td className="py-2">
                          <Link to={`/owner/users/${u.uid}`} className="text-primary hover:underline">
                            {u.displayName || '—'}
                          </Link>
                        </td>
                        <td className="py-2">{u.email}</td>
                        <td className="py-2">{roleLabel(u.role)}</td>
                        <td className="py-2 text-muted-foreground">{companyName(u.companyId)}</td>
                        <td className="py-2">{statusLabel(u.status)}</td>
                        <td className="py-2">
                          <Button variant="outline" size="sm" onClick={() => setEditingUser(u)}>
                            Editar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <UserEditModal
        open={!!editingUser}
        onOpenChange={(open) => !open && setEditingUser(null)}
        user={editingUser}
        companies={companies}
        onSaved={loadUsers}
      />
      <AddUserModal
        open={addUserOpen}
        onOpenChange={setAddUserOpen}
        companies={companies}
        onSaved={loadUsers}
      />
    </div>
  );
}
